// ─────────────────────────────────────────────────────────────────────────────
//  Scale barcode parser (Phase A1) — pure, side-effect free, unit-testable.
//
//  Standalone retail scales print EAN-13 labels with the value embedded:
//
//      [prefix 2][ PLU 4-5 ][ value ][ check 1 ]   = 13 digits
//
//  The payload between prefix and check digit is always 10 digits, so the
//  value width is derived: 10 − pluDigits (5 digits for 5-digit PLUs,
//  6 digits for 4-digit PLUs). Value is either grams (weight_g) or tetri
//  (price_tetri), per the org's scale settings.
//
//  A code that fails ANY rule (length, prefix, EAN mod-10 check) returns
//  null and the normal product-barcode lookup proceeds unchanged.
// ─────────────────────────────────────────────────────────────────────────────

export type ScaleValueType = 'weight_g' | 'price_tetri'

export interface ScaleConfig {
  enabled: boolean
  /** comma-separated 2-digit prefixes, e.g. "21,22" (stored as a settings-KV string) */
  prefixes: string
  pluDigits: 4 | 5
  valueType: ScaleValueType
}

export interface ParsedScaleCode {
  plu: string
  /** raw embedded number (grams or tetri, per valueType) */
  value: number
  valueType: ScaleValueType
}

export const SCALE_DEFAULTS = { prefixes: '21,22', pluDigits: 5 as const, valueType: 'weight_g' as const }

/** EAN-13 mod-10: x1+3x2+x3+3x4… over the first 12 digits. */
export function ean13CheckDigit(first12: string): number {
  let sum = 0
  for (let i = 0; i < 12; i++) {
    const d = first12.charCodeAt(i) - 48
    sum += i % 2 === 0 ? d : d * 3
  }
  return (10 - (sum % 10)) % 10
}

export function parseScaleBarcode(raw: string, cfg: ScaleConfig): ParsedScaleCode | null {
  if (!cfg.enabled) return null
  const code = raw.trim()
  if (!/^\d{13}$/.test(code)) return null

  const prefixes = cfg.prefixes.split(',').map(p => p.trim()).filter(p => /^\d{2}$/.test(p))
  if (!prefixes.includes(code.slice(0, 2))) return null

  if (ean13CheckDigit(code.slice(0, 12)) !== code.charCodeAt(12) - 48) return null

  const pluDigits = cfg.pluDigits === 4 ? 4 : 5
  const plu = code.slice(2, 2 + pluDigits)
  const value = Number(code.slice(2 + pluDigits, 12))
  return { plu, value, valueType: cfg.valueType }
}

/** What went wrong with a label, as a dictionary key — the till says it in its language. */
export type ScaleWarning = 'scale.zeroWeight' | 'scale.noPrice' | 'scale.zeroPrice'

/**
 * Cart quantity for a parsed scale code.
 *  - weight_g:    grams → kg, 3 decimals (0.001 kg resolution, matches NUMERIC(12,3))
 *  - price_tetri: qty = embedded price ÷ unit price, 3 decimals.
 * Returns { qty } or { qty: 1, warning } when the price math is impossible.
 */
export function scaleQuantity(
  parsed: ParsedScaleCode,
  unitPriceGel: number,
): { qty: number; warning?: ScaleWarning } {
  if (parsed.valueType === 'weight_g') {
    const qty = Math.round(parsed.value) / 1000
    return qty > 0 ? { qty } : { qty: 1, warning: 'scale.zeroWeight' }
  }
  // price_tetri
  if (!unitPriceGel || unitPriceGel <= 0) {
    return { qty: 1, warning: 'scale.noPrice' }
  }
  const qty = Math.round((parsed.value / 100 / unitPriceGel) * 1000) / 1000
  return qty > 0 ? { qty } : { qty: 1, warning: 'scale.zeroPrice' }
}

/** Build a valid sample scale EAN-13 (used by tests + the sample-label script). */
export function buildScaleBarcode(prefix: string, plu: string, value: number, pluDigits: 4 | 5): string {
  const valueDigits = 10 - pluDigits
  const first12 = prefix + plu.padStart(pluDigits, '0') + String(value).padStart(valueDigits, '0')
  if (first12.length !== 12) throw new Error(`bad parts: ${first12}`)
  return first12 + String(ean13CheckDigit(first12))
}

/**
 * A product's scale code as the till will match it: digits only, leading
 * zeros dropped, at most 5 digits (what a 5-digit-code label can carry).
 * Labels print the code zero-padded — Nikora's „კოდი:004852" travels as 04852
 * — and the field used to keep the first 5 typed digits, so „004852" became
 * „00485" and never matched (2026-10-04).
 */
export function normalizePlu(input: string): string {
  return input.replace(/\D/g, '').replace(/^0+/, '').slice(0, 5)
}
